/**
 * Orden de las columnas del kanban.
 *
 * Reglas del diseño (pantallas 06 y 07):
 * - "Fijar" SIEMPRE manda la columna al extremo izquierdo, sin importar dónde
 *   estaba. Fijar una segunda columna la deja a la izquierda de la anterior.
 * - "Mandar al final" la manda al extremo derecho.
 * - El selector del título intercambia el LUGAR de dos columnas.
 *
 * Las preferencias se guardan por usuario y por vista, así que estas funciones
 * son puras: reciben el estado y devuelven el estado nuevo.
 */

export function emptyColumnPreferences () {
  return { pinned: [], sentToEnd: [], order: [] }
}

function normalize (preferences) {
  const base = emptyColumnPreferences()
  if (!preferences) return base

  return {
    pinned: Array.isArray(preferences.pinned) ? preferences.pinned.map(String) : [],
    sentToEnd: Array.isArray(preferences.sentToEnd) ? preferences.sentToEnd.map(String) : [],
    order: Array.isArray(preferences.order) ? preferences.order.map(String) : []
  }
}

/**
 * Aplica pin / mandar al final / orden manual sobre las columnas que llegaron
 * del backend, sin perder ninguna ni inventar las que ya no existen.
 */
export function applyColumnOrder (columns, preferences) {
  const prefs = normalize(preferences)
  const byKey = new Map(columns.map(column => [String(column.key), column]))

  const take = keys => keys
    .map(key => byKey.get(key))
    .filter(Boolean)

  const pinned = take(prefs.pinned)
  const sentToEnd = take(prefs.sentToEnd)

  const used = new Set([...pinned, ...sentToEnd].map(column => String(column.key)))

  const ordered = take(prefs.order).filter(column => !used.has(String(column.key)))
  ordered.forEach(column => used.add(String(column.key)))

  const rest = columns.filter(column => !used.has(String(column.key)))

  return [...pinned, ...ordered, ...rest, ...sentToEnd]
}

/**
 * Fijar: al extremo izquierdo, siempre.
 */
export function pinColumn (preferences, key) {
  const prefs = normalize(preferences)
  const target = String(key)

  return {
    ...prefs,
    pinned: [target, ...prefs.pinned.filter(item => item !== target)],
    sentToEnd: prefs.sentToEnd.filter(item => item !== target),
    order: prefs.order.filter(item => item !== target)
  }
}

export function unpinColumn (preferences, key) {
  const prefs = normalize(preferences)
  const target = String(key)

  return { ...prefs, pinned: prefs.pinned.filter(item => item !== target) }
}

/**
 * Mandar al final: al extremo derecho. Deja de estar fijada si lo estaba.
 */
export function sendColumnToEnd (preferences, key) {
  const prefs = normalize(preferences)
  const target = String(key)

  return {
    pinned: prefs.pinned.filter(item => item !== target),
    order: prefs.order.filter(item => item !== target),
    sentToEnd: [...prefs.sentToEnd.filter(item => item !== target), target]
  }
}

/**
 * Intercambia el lugar de dos columnas. Se materializa el orden completo para
 * que el intercambio sobreviva al siguiente refresco del tablero.
 */
export function swapColumns (preferences, columns, keyA, keyB) {
  const prefs = normalize(preferences)
  const current = applyColumnOrder(columns, prefs).map(column => String(column.key))

  const indexA = current.indexOf(String(keyA))
  const indexB = current.indexOf(String(keyB))
  if (indexA === -1 || indexB === -1 || indexA === indexB) {
    return prefs
  }

  const next = current.slice()
  next[indexA] = current[indexB]
  next[indexB] = current[indexA]

  // El intercambio manda sobre el pin y sobre el "mandar al final": si el
  // usuario mueve a mano una columna fijada, deja de estar fijada.
  const moved = [String(keyA), String(keyB)]

  return {
    pinned: prefs.pinned.filter(item => !moved.includes(item)),
    sentToEnd: prefs.sentToEnd.filter(item => !moved.includes(item)),
    order: next
  }
}

export function isPinned (preferences, key) {
  return normalize(preferences).pinned.includes(String(key))
}

export function isSentToEnd (preferences, key) {
  return normalize(preferences).sentToEnd.includes(String(key))
}
